import React from 'react';
import _ from 'underscore';
import { FormattedMessage } from 'react-intl';
import pluralize from 'pluralize';
import D13nStore from 'stores/plugins/d13n';
import NavigationActionCreator from 'action_creators/navigation';
import ModalActionCreator from 'action_creators/modal';
import Popup from 'views/shared/ui/popup';
import Cover from 'views/plugins/checkout/mobile/screens/cover';

class ConfirmationMobileSummary extends React.PureComponent {
  static displayName = 'ConfirmationMobile-MessageScreen-Plugins';

  _transitionToMessage = () => {
    NavigationActionCreator.transitionBack();
  };

  _bookingFee = () => {
    if (this.props.pricing.includes_booking_fee) {
      const fee = this.props.pricing.booking_fee.total_price;
      return (
        <li>
          <span className="label">
            <FormattedMessage
              defaultMessage="Service Fee"
              description="Service fee line item label"
            />
          </span>
          <span className="value">{fee}</span>
        </li>
      );
    }
  };

  _additionalTax = () => {
    if (this.props.pricing.includes_additional_tax) {
      return (
        <li>
          <span className="label">
            {this.props.pricing.additional_tax_label}
          </span>
          <span className="value">
            {roundPrice(this.props.additional_tax_formatted)}
          </span>
        </li>
      );
    }
  };

  _cashPaymentNotice = () => {
    if (this.props.pricing.paid_in_cash) {
      return (
        <li>
          <span className="cashPaymentNotice">
            <FormattedMessage
              id="AMOUNT_DUE_IN_CASH_AT_ARRIVAL"
              defaultMessage="Amount due in cash at your arrival on <strong>{date}</strong>."
              values={{
                date: this.props.date,
                strong: (chunks) => <strong>{chunks}</strong>,
              }}
            />
          </span>
        </li>
      );
    }
  };

  _guestsPricing = () => {
    return (
      <li>
        <span className="label">
          {this.props.pricing.guests_pricing.price_per_guest} x{' '}
          {this.props.pricing.guests_pricing.number_of_guests}{' '}
          {pluralize(
            D13nStore.d('guest'),
            this.props.pricing.guests_pricing.number_of_guests
          )}
        </span>
        <span className="value">
          {this.props.pricing.guests_pricing.total_price}
        </span>
      </li>
    );
  };

  _addonPricing = (addonProps, index) => {
    return (
      <li>
        <span className="addonName">{addonProps.description}</span>
        <span className="label">
          <span className="addonPrice">{addonProps.price_per_guest} x </span>
          <span className="addonGuests">
            {addonProps.number_of_guests}{' '}
            {pluralize(D13nStore.d('guest_addon'), addonProps.number_of_guests)}
          </span>
        </span>
        <span className="value">{addonProps.total_price}</span>
      </li>
    );
  };

  _addonsPricing = () => {
    const self = this;

    return _.map(this.props.pricing.addons_pricing, (addon, index) => {
      return self._addonPricing(addon, index);
    });
  };

  _discountPricing = () => {
    if (this.props.pricing.includes_discount) {
      return (
        <li>
          <span className="label">
            <FormattedMessage
              defaultMessage="Discount"
              description="Discount line item label"
            />
            :{this.props.pricing.discount_pricing.description}
          </span>
          <span className="value">
            {this.props.pricing.discount_pricing.total_price}
            <span className="toggleIconContainer">
              (<i className="icon af-minus-ar" />)
            </span>
          </span>
        </li>
      );
    }
  };

  _depositDue = () => {
    return (
      <li>
        <span className="label">
          <FormattedMessage
            defaultMessage="Deposit Due"
            description="Deposit due line item label"
          />
        </span>
        <span className="value">
          {this.props.pricing.deposit_pricing.total_price}
        </span>
      </li>
    );
  };

  _showCancellationPolicyPopup = (e) => {
    e.preventDefault();
    ModalActionCreator.show({
      content: this._cancellationPolicyPopupContent(),
      triggeringElement: e.currentTarget,
      className: 'small ConfirmationSummary-CancellationPolicy-MobileModal',
    });
  };

  _cancellationPolicyPopupContent = () => {
    return (
      <div className="CancellationPolicy-Modal-content">
        <div>
          <b>
            <FormattedMessage
              defaultMessage="Cancellation Policy"
              description="Cancellation policy popup heading"
            />
          </b>
        </div>
        <hr />
        <p>{this.props.cancellation_policy}</p>
      </div>
    );
  };

  _renderCancellationPolicyTrigger = () => {
    return (
      <a
        href="#"
        className="dotted"
        onClick={this._showCancellationPolicyPopup}
      >
        {_.str.truncate(this.props.short_cancellation_policy, 40)}
      </a>
    );
  };

  _renderCancellationPolicy = () => {
    if (!this.props.pricing.paid_in_cash) {
      return (
        <li className="separator">
          <span className="label">
            <FormattedMessage
              defaultMessage="Cancellation Policy"
              description="Cancellation policy line item label"
            />
          </span>
          <span className="value condensed">
            {this.props.has_cancellation ? (
              this._renderCancellationPolicyTrigger()
            ) : (
              <FormattedMessage
                defaultMessage="No Cancellations"
                description="No cancellation policy message"
              />
            )}
          </span>
        </li>
      );
    }
  };

  _onlineDiscountPricing = () => {
    if (this.props.pricing.includes_online_discount) {
      return (
        <li>
          <span className="label">
            <FormattedMessage
              defaultMessage="Online Payment Discount"
              description="Online payment discount line item label"
            />
            :
          </span>
          <span className="value">
            {this.props.pricing.online_discount}
            <span className="toggleIconContainer">
              (<i className="icon af-minus-ar" />)
            </span>
          </span>
        </li>
      );
    }
  };

  _coverProps = () => {
    return {
      forInvoice: this.props.for_invoice,
      tourPicture: this.props.tour_picture,
      guideAvatar: this.props.guide_avatar,
      tourName: this.props.tour_name_translation,
      guideName: this.props.guide_full_name,
    };
  };

  render() {
    return (
      <div className="ConfirmationMobile-Screen ConfirmationMobile-Summary">
        <div className="ConfirmationMobile-ScreenHeader transition-ctx">
          <i
            className="icon af-chevron back-button"
            onClick={this._transitionToMessage}
          />
          <FormattedMessage
            defaultMessage="Booking Details"
            description="Booking details page header"
          />
        </div>
        <div className="ConfirmationMobile-ScreenContent transition-ctx">
          <Cover {...this._coverProps()} />
          <div className="ConfirmationMobile-PageContent">
            <ul className="ConfirmationMobile-PricingBreakdown">
              <li>
                <span className="label">
                  <FormattedMessage id="DATE" defaultMessage="Date" />
                </span>
                <span className="value">{this.props.date}</span>
              </li>
              <li>
                <span className="label">
                  <FormattedMessage id="TIME" defaultMessage="Time" />
                </span>
                <span className="value">{this.props.time}</span>
              </li>
              <li>
                <span className="label">
                  <FormattedMessage id="DURATION" defaultMessage="Duration" />
                </span>
                <span className="value">{this.props.duration}</span>
              </li>
              <li>
                <span className="label">
                  <FormattedMessage
                    id="NUMBER_OF_GUESTS"
                    defaultMessage="Number of Guests"
                  />
                </span>
                <span className="value">
                  {this.props.guests}{' '}
                  {pluralize(D13nStore.d('guest_addon'), this.props.guests)}
                </span>
              </li>
              {this._renderCancellationPolicy()}
              <li>
                <span className="label">
                  <FormattedMessage id="TOTAL" defaultMessage="Total" />
                </span>
                <span className="value">
                  <span className="large">
                    <strong>{this.props.pricing.subtotal}</strong>
                  </span>
                </span>
              </li>
              {this._guestsPricing()}
              {this._addonsPricing()}
              {this._discountPricing()}
              {this._additionalTax()}
              {this._onlineDiscountPricing()}

              {this._bookingFee()}

              {this._depositDue()}
              {this._cashPaymentNotice()}
            </ul>
          </div>
        </div>
      </div>
    );
  }
}

export default ConfirmationMobileSummary;
